import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Tag, ShieldCheck, Loader2 } from "lucide-react";
import { api, inr, getSessionId } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Overline } from "@/components/Reveal";

const FIELDS = [
  { name: "full_name", label: "Full Name", span: 2 },
  { name: "phone", label: "Phone Number", span: 1 },
  { name: "email", label: "Email Address", span: 1 },
  { name: "line1", label: "Address Line 1", span: 2 },
  { name: "line2", label: "Address Line 2 (optional)", span: 2, optional: true },
  { name: "city", label: "City", span: 1 },
  { name: "state", label: "State", span: 1 },
  { name: "pincode", label: "Pincode", span: 1 },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { cart, refreshCart } = useStore();
  const navigate = useNavigate();
  const sessionId = getSessionId();
  const { items, totals } = cart;

  const [form, setForm] = useState({ full_name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "" });
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [placing, setPlacing] = useState(false);

  const discount = applied?.discount || 0;
  const subtotal = totals.subtotal;
  const delivery = (subtotal - discount) >= 499 || subtotal === 0 ? 0 : 40;
  const grandTotal = Math.max(0, subtotal - discount + delivery);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-apply a pending coupon (e.g. COMBO3 from the combo builder)
  useEffect(() => {
    const pending = localStorage.getItem("dfk_pending_coupon");
    if (pending && !applied && subtotal > 0) {
      api.post("/coupon/validate", { code: pending, subtotal })
        .then(({ data }) => { setCoupon(pending); setApplied(data); })
        .catch(() => {})
        .finally(() => localStorage.removeItem("dfk_pending_coupon"));
    }
  }, [subtotal, applied]);

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupon/validate", { code: coupon, subtotal });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied`, { description: data.description });
    } catch (e) {
      setApplied(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const valid = FIELDS.every((f) => f.optional || form[f.name].trim()) &&
    /^\d{10}$/.test(form.phone) && /\S+@\S+\.\S+/.test(form.email) && /^\d{6}$/.test(form.pincode);

  const placeOrder = async () => {
    if (!valid) return toast.error("Please fill all fields correctly (valid phone, email & 6-digit pincode).");
    if (items.length === 0) return toast.error("Your cart is empty.");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { session_id: sessionId, address: form, coupon: applied?.code || null });
      const order = data.order;

      if (!data.simulated && data.razorpay_key_id && order.razorpay_order_id) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Razorpay failed to load");
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: order.amount_paise,
          currency: "INR",
          name: "Dignity Food Kitchen",
          description: `Order ${order.order_number}`,
          order_id: order.razorpay_order_id,
          prefill: { name: form.full_name, email: form.email, contact: form.phone },
          theme: { color: "#D4AF37" },
          handler: async (res) => {
            await api.post("/orders/verify", { order_id: order.order_number, ...res });
            await refreshCart();
            navigate(`/order/${order.order_number}`);
          },
          modal: { ondismiss: () => setPlacing(false) },
        });
        rzp.open();
        return;
      }

      // Simulated payment mode
      await new Promise((r) => setTimeout(r, 900));
      await api.post("/orders/verify", { order_id: order.order_number });
      await refreshCart();
      navigate(`/order/${order.order_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order. Try again.");
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-28">
      <div className="mx-auto max-w-6xl px-5 pb-24 lg:px-8">
        <Overline>Almost There</Overline>
        <h1 className="mt-3 font-display text-5xl tracking-tight text-cream sm:text-7xl">CHECKOUT</h1>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          {/* Address form */}
          <div>
            <h2 className="font-oswald text-xl uppercase tracking-wide text-gold">Delivery Address</h2>
            <div className="mt-6 grid grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                  <label className="mb-1.5 block font-oswald text-[11px] uppercase tracking-[0.15em] text-white/50">{f.label}</label>
                  <input value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
                    data-testid={`checkout-${f.name}`}
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-cream outline-none transition-colors focus:border-gold"
                    placeholder={f.label} />
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/50">
              <ShieldCheck size={18} className="text-gold" /> Payments are secure. Powered by Razorpay.
            </div>
          </div>

          {/* Order summary */}
          <div className="rounded-3xl border border-white/10 bg-forest-deep p-6 lg:sticky lg:top-28 lg:self-start">
            <h2 className="font-oswald text-xl uppercase tracking-wide text-gold">Order Summary</h2>
            <div className="mt-5 max-h-64 space-y-3 overflow-y-auto pr-1">
              {items.map(({ product, qty }) => (
                <div key={product.id} className="flex gap-3">
                  <img src={product.image} alt={product.name} className="h-16 w-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-oswald text-sm text-cream leading-tight">{product.name}</div>
                    <div className="text-xs" style={{ color: product.accent }}>{product.flavour} · × {qty}</div>
                  </div>
                  <span className="font-oswald text-sm text-cream">{inr(product.price * qty)}</span>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-white/40">Your cart is empty.</p>}
            </div>

            {/* Coupon */}
            <div className="mt-5 flex gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-white/15 px-4">
                <Tag size={15} className="text-gold" />
                <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  data-testid="coupon-input" placeholder="Coupon code"
                  className="w-full bg-transparent py-2.5 text-sm text-cream outline-none placeholder:text-white/30" />
              </div>
              <button onClick={applyCoupon} data-testid="apply-coupon-btn"
                className="rounded-full border border-gold px-5 font-oswald text-xs uppercase tracking-wide text-gold hover:bg-gold hover:text-black">
                Apply
              </button>
            </div>
            <p className="mt-2 text-[11px] text-white/30">Try CRUNCH10, DIGNITY50 or FIRSTBITE</p>

            <div className="mt-5 space-y-2 border-t border-white/10 pt-5 text-sm">
              <Row label="Subtotal" value={inr(subtotal)} />
              {discount > 0 && <Row label={`Discount (${applied.code})`} value={`- ${inr(discount)}`} green />}
              <Row label="Delivery" value={delivery === 0 ? "FREE" : inr(delivery)} />
              <div className="flex items-center justify-between border-t border-white/10 pt-3">
                <span className="font-oswald text-lg text-cream">TOTAL</span>
                <span className="font-display text-3xl text-gold" data-testid="grand-total">{inr(grandTotal)}</span>
              </div>
            </div>

            <button onClick={placeOrder} disabled={placing || items.length === 0} data-testid="place-order-btn"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full gold-gradient py-4 font-oswald text-sm uppercase tracking-[0.15em] text-black transition-transform hover:scale-[1.02] disabled:opacity-50">
              {placing ? <><Loader2 size={18} className="animate-spin" /> Processing…</> : `Pay ${inr(grandTotal)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, green }) => (
  <div className="flex items-center justify-between text-white/60">
    <span>{label}</span><span className={green ? "text-green-400" : "text-cream"}>{value}</span>
  </div>
);
